import React, { useEffect, useState, useRef } from 'react';
import { Map, Source, Layer } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Button,
  Box,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DetailsDrawer from './DetailsDrawer';
import debounce from 'lodash/debounce';
import { MAP_BOX_API_KEY, AssetType } from '../../services/AppConstant';
import { getAssets } from '../../api';
const icons = {
  TrafficSignal: {
    red: '/signal/red_signal.png',
    yellow: '/signal/yellow_signal.png',
    green: '/signal/green_signal.png',
    no_op: '/signal/red_signal.png',
  },
  Camera: {
    normal: '/camera/camera_normal.png',
    caution: '/camera/camera_blue.png',
    critical: '/camera/camera_alert.png',
  },
  SpeedDetector: {
    active: '/speed_detector/speed_active.png',
    inactive: '/speed_detector/speed_inactive.png',
  },
  MessageBoard: {
    fixed: '/message_board/fixed_board.png',
    mobile: '/message_board/mobile_board.png',
  },

};


const TrafficMap = () => {
  const [trafficSignals, setTrafficSignals] = useState([]);
  const [trafficCameras, setTrafficCameras] = useState([]);
  const [speedDetectors, setSpeedDetectors] = useState([]);
  const [messageBoards, setMessageBoards] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [events, setEvents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isEventDrawerOpen, setIsEventDrawerOpen] = useState(true);
  const [viewport, setViewport] = useState({
    latitude: 30.2672, // Default latitude (Austin, TX)
    longitude: -97.7431, // Default longitude (Austin, TX)
    zoom: 12,
  });
  // Create a ref for the map
  const mapRef = useRef(null);
  const mapboxToken = MAP_BOX_API_KEY; // Replace with your Mapbox token
  // Load MUI icons as images in Mapbox
  const loadIcons = (map) => {

    if (mapLoaded) return;
    setMapLoaded(true);
    Object.entries(icons.TrafficSignal).forEach(([status, url]) => {
      map.loadImage(url, (error, image) => {
        if (!error && image) {
          map.addImage(`traffic-signal-icon-${status}`, image);
        }
      });
    });

    Object.entries(icons.Camera).forEach(([status, url]) => {
      map.loadImage(url, (error, image) => {
        if (!error && image) {
          map.addImage(`camera-icon-${status}`, image);
        }
      });
    });
    Object.entries(icons.SpeedDetector).forEach(([status, url]) => {
      map.loadImage(url, (error, image) => {
        if (!error && image) {
          map.addImage(`speed-detector-icon-${status}`, image);
        }
      });
    });
    Object.entries(icons.MessageBoard).forEach(([status, url]) => {
      map.loadImage(url, (error, image) => {
        if (!error && image) {
          map.addImage(`message-board-icon-${status}`, image);
        }
      });
    });


  };


  const processEvents = (incidents) => {
    const events = incidents.map((incident) => {

      incident.location = incident.sourceId.Location.coordinates;
      incident.type = 'critical';
      return incident;
    });
    setEvents(events);
  }

  // WebSocket connection
  useEffect(() => {
    //   const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    //   const host = window.location.hostname; // Use hostname without port
    //   const port = window.location.port === '3000' ? '5000' : window.location.port; // Use port 5000 for local dev
    //   const wsPath = '/ws'; // Ensure this matches the server's WebSocket path

    //   const ws = new WebSocket(`${protocol}//${host}:${port}${wsPath}`);
    //  // const ws = new WebSocket('wss://cfinvma1l0.execute-api.us-east-1.amazonaws.com/production/');
    //   ws.onopen = () => {
    //     console.log('WebSocket connection established');
    //   };

    //   ws.onmessage = (message) => {
    //     const data = JSON.parse(message.data);
    //     if (data.type == 'initial') {
    //       setTrafficSignals(data.trafficSignals);
    //       setTrafficCameras(data.trafficCameras);
    //       setSpeedDetectors(data.speedDetectors);
    //       setMessageBoards(data.trafficMessageBoards);
    //       processEvents(data.incidents, data.trafficCameras);
    //     }
    //     if (data.type == 'incidents') {
    //       processEvents(data.incidents, trafficCameras);
    //     }
    //   };

    //   ws.onclose = () => {
    //     console.log('WebSocket connection closed');
    //   };
    setTimeout(() => {
      if (mapRef.current) {
        const map = mapRef.current.getMap();
        loadIcons(map);
      }
    }, 500)

    const fetchAssets = async () => {
      try {
        const response = await getAssets();
        let data = response.data;
        setTrafficSignals(data.trafficSignals);
        setTrafficCameras(data.trafficCameras);
        setSpeedDetectors(data.speedDetectors);
        setMessageBoards(data.trafficMessageBoards);
        processEvents(data.incidents, data.trafficCameras);
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
    };
    fetchAssets();

    // return () => {
    //   ws.close(); // Clean up WebSocket connection on component unmount
    // };
  }, []);


  const handleEventDrawerToggle = () => {
    setIsEventDrawerOpen(!isEventDrawerOpen);
  };

  // Handle icon click to open the DetailsDrawer
  const handleIconClick = (event) => {
    const feature = event.features[0];
    if (feature) {

      const data = JSON.parse(feature.properties.data);
      const type = feature.properties.type; // Get the type from properties

      // Set the selected item with both data and type
      setSelectedItem({ ...data, type });
      setIsDrawerOpen(true);
    }
  };


  // Close the side panel
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedItem(null);
  };



  // Create GeoJSON data for clustering
  const createGeoJSONData = () => {
    const features = [];

    trafficSignals.forEach((signal) => {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [signal.Location.coordinates[0] + 0.0001, signal.Location.coordinates[1] + 0.0001],
        },
        properties: {
          id: signal._id,
          name: signal.name,
          status: signal.status ? signal.status.toLowerCase() : 'no_op',
          data: signal,
          type: AssetType.TRAFFIC_SIGNAL,
        },
      });
    });

    trafficCameras.forEach((camera) => {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [camera.Location.coordinates[0] - 0.0001, camera.Location.coordinates[1] - 0.0001],
        },
        properties: {
          id: camera._id,
          name: camera.LocationName,
          status: camera.status ? camera.status.toLowerCase() : 'normal',
          data: camera,
          type: AssetType.CAMERA,
        },
      });
    });

    speedDetectors.forEach((detector) => {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [detector.Location.coordinates[0] - 0.00015, detector.Location.coordinates[1] - 0.00015],
        },
        properties: {
          id: detector._id,
          name: detector.LocationName,
          status: 'active',
          data: detector,
          type: AssetType.SPEED_DETECTOR,
        },
      });
    });
    messageBoards.forEach((board) => {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [board.Location.coordinates[0] + 0.00015, board.Location.coordinates[1] - 0.00015],
        },
        properties: {
          id: board._id,
          name: board.LocationName,
          status: 'fixed',
          data: board,
          type: AssetType.MESSAGE_BOARD,
        },
      });
    });



    return {
      type: 'FeatureCollection',
      features,
    };
  };

  const geoJSONData = createGeoJSONData();

  let activeAnimations = {};

  // Function to animate the ripple effect
  const animateRipple = (map, eventId) => {
    let radius = 5; // Starting radius
    let increasing = true; // Direction of radius change

    const animate = () => {
      // Stop the animation if this event is no longer in activeAnimations
      if (!activeAnimations[eventId]) {
        return;
      }

      // Update radius
      if (increasing) {
        radius += 0.5;
        if (radius >= 20) increasing = false;
      } else {
        radius -= 0.5;
        if (radius <= 5) increasing = true;
      }

      // Update the circle's radius dynamically
      if (map.getLayer(`ripple-${eventId}`)) {
        map.setPaintProperty(`ripple-${eventId}`, 'circle-radius', radius);
      }

      // Schedule the next frame
      activeAnimations[eventId].frameId = requestAnimationFrame(animate);
    };

    // Start the animation
    activeAnimations[eventId] = { frameId: requestAnimationFrame(animate) };
  };

  // Function to clear all animations
  const clearAnimations = (map) => {
    Object.keys(activeAnimations).forEach((eventId) => {
      // Cancel the animation frame
      cancelAnimationFrame(activeAnimations[eventId].frameId);

      // Remove the layer and source
      if (map.getLayer(`ripple-${eventId}`)) {
        map.removeLayer(`ripple-${eventId}`);
      }
      if (map.getSource(`ripple-${eventId}`)) {
        map.removeSource(`ripple-${eventId}`);
      }
    });

    // Clear the activeAnimations object
    activeAnimations = {};
  };

  // Add ripple effects programmatically
  useEffect(() => {
    if (!mapRef.current || !events.length) return;

    const map = mapRef.current.getMap();

    clearAnimations(map);

    // Clear previous layers if any
    events.forEach((event) => {
      if (map.getLayer(`ripple-${event._id}`)) {
        map.removeLayer(`ripple-${event._id}`);
        map.removeSource(`ripple-${event._id}`);
      }
    });

    // Add new layers for each event
    events.forEach((event) => {
      map.addSource(`ripple-${event._id}`, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: event.location,
          },
        },
      });

      map.addLayer({
        id: `ripple-${event._id}`,
        type: 'circle',
        source: `ripple-${event._id}`,
        paint: {
          'circle-radius': {
            stops: [
              [12, 5], // Radius at zoom level 12
              [15, 20], // Radius at zoom level 15
            ],
          },
          'circle-color': event.type === 'critical'
            ? 'rgba(255, 0, 0, 0.5)'
            : event.type === 'warning'
              ? 'rgba(255, 255, 0, 0.5)'
              : 'rgba(0, 0, 255, 0.5)', // Dynamic color based on type
          'circle-stroke-width': 2,
          'circle-stroke-color': event.type === 'critical'
            ? 'rgba(255, 0, 0, 0.8)'
            : event.type === 'warning'
              ? 'rgba(255, 255, 0, 0.8)'
              : 'rgba(0, 0, 255, 0.8)',
          'circle-stroke-opacity': 0.8,
        },
      });
      animateRipple(map, event._id);
    });

    return () => {
      clearAnimations(map);
    //  setEvents([]); // Clear the events state
    };
  }, [events, mapLoaded]);


  // Debounce function to limit API calls
  const debouncedSearch = useRef(
    debounce((query) => {
      if (query.trim() === '') {
        setSearchResults([]);
        return;
      }
      // Filter items based on the search query
      const results = [...trafficSignals, ...trafficCameras].filter((item) =>
        item.name.toLowerCase().includes(query.toLowerCase())
      );
      setSearchResults(results);
    }, 300) // 300 ms delay
  ).current;

  useEffect(() => {
    // Trigger the debounced search function when searchQuery changes
    debouncedSearch(searchQuery);
  }, [searchQuery, debouncedSearch]);
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle selecting a search result
  const handleSelectResult = (result) => {
    if (mapRef.current && result) {
      mapRef.current.flyTo({
        center: [result.Location.coordinates[0], result.Location.coordinates[1]],
        zoom: 15,
        speed: 1.5,
      });
    }
    setSearchQuery(result.name); // Update search input to selected item
    setSearchResults([]); // Clear search results
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '10px', background: '#fff', zIndex: 1, position: 'relative' }}>
        <input
          type="text"
          placeholder="Search for Traffic Signal or Camera"
          value={searchQuery}
          onChange={handleSearchChange}
          style={{ padding: '5px', width: '300px' }}
        />
        {searchResults.length > 0 && (
          <ul style={{ listStyleType: 'none', padding: '0', margin: '5px 0', border: '1px solid #ccc', background: '#fff', position: 'absolute', width: '300px', maxHeight: '150px', overflowY: 'auto' }}>
            {searchResults.map((result) => (
              <li
                key={result._id}
                onClick={() => handleSelectResult(result)}
                style={{ padding: '5px', cursor: 'pointer' }}
              >
                {result.name}
              </li>
            ))}
          </ul>
        )}
      </div>
      <Map
        initialViewState={viewport}
        ref={mapRef}
        style={{ width: '100%', height: '100vh' }}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        mapboxAccessToken={mapboxToken}
        onViewportChange={(newViewport) => setViewport(newViewport)}
        onLoad={(event) => {
          loadIcons(event.target)
        }}
        interactiveLayerIds={['unclustered-point']}
        onClick={handleIconClick} // Handle icon click
      >
        {/* Clustered Source and Layer */}
        <Source
          id="assets"
          type="geojson"
          data={geoJSONData}
          cluster={true}
          clusterMaxZoom={15} // Max zoom to cluster points on
          clusterRadius={50} // Radius of each cluster
        >
          <Layer
            id="clusters"
            type="circle"
            source="assets"
            filter={['has', 'point_count']}
            paint={{
              'circle-color': '#51bbd6',
              'circle-radius': [
                'step',
                ['get', 'point_count'],
                20,
                100,
                30,
                750,
                40,
              ],
            }}
          />
          <Layer
            id="cluster-count"
            type="symbol"
            source="assets"
            filter={['has', 'point_count']}
            layout={{
              'text-field': '{point_count_abbreviated}',
              'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
              'text-size': 12,
            }}
          />
          <Layer
            id="icon-background"
            type="circle"
            source="assets"
            filter={['!', ['has', 'point_count']]}
            paint={{
              'circle-color': '#ffffff', // Background color
              'circle-radius': 25,       // Adjust radius to fit your icon size
              'circle-opacity': 0.9,     // Adjust opacity as needed
            }}
          />
          <Layer
            id="unclustered-point"
            type="symbol"
            source="assets"
            filter={['!', ['has', 'point_count']]}
            layout={{
              'icon-image': [
                'case',
                ['==', ['get', 'type'], AssetType.TRAFFIC_SIGNAL],
                ['concat', 'traffic-signal-icon-', ['get', 'status']],
                ['==', ['get', 'type'], AssetType.CAMERA],
                ['concat', 'camera-icon-', ['get', 'status']],
                ['==', ['get', 'type'], AssetType.MESSAGE_BOARD],
                ['concat', 'message-board-icon-', ['get', 'status']],
                ['concat', 'speed-detector-icon-', ['get', 'status']],
              ],
              'icon-size': 0.12,
            }}
          />
        </Source>
      </Map>

      {/* Event Drawer */}
      <Drawer
        variant="persistent"
        anchor="right"
        open={isEventDrawerOpen}
        PaperProps={{
          style: { width: '300px' },
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px',
          }}
        >
          <Typography variant="h6">Events</Typography>
          <IconButton onClick={handleEventDrawerToggle}>
            <ChevronRightIcon />
          </IconButton>
        </div>
        <List>
          {events.map((event) => {
            const {
              sourceType,
              sourceId,
              cameraFeedUrl,
              workflowId,
              eventId
            } = event;

            return (
              <ListItem key={event._id} alignItems="flex-start">
                <ListItemText
                  primary={
                    <>
                      <strong>Event Type:</strong> {eventId.type}<br />
                      <strong>Camera Location:</strong> {sourceId["Location Name"] || "N/A"}<br />
                      <strong>Camera ID:</strong> {sourceId["Camera ID"]}<br />
                      <strong>Description:</strong> {eventId.description || "No description available."}
                      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => {
                            if (mapRef.current) {
                              mapRef.current.flyTo({
                                center: [sourceId.Location.coordinates[0], sourceId.Location.coordinates[1]],
                                zoom: 18, // Set the zoom level to 18
                                speed: 1.2, // Optional: Adjust the speed of the animation
                              });
                            }
                          }}
                        >
                          Locate
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={() => window.location.href = `/incident/${event._id}`}
                        >
                          View
                        </Button>
                      </Box>
                    </>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </Drawer>

      {/* Toggle Button */}
      {!isEventDrawerOpen && (
        <Button
          variant="contained"
          style={{
            position: 'absolute',
            top: '50%',
            right: 0,
            transform: 'translateY(-50%)',
            zIndex: 10,
          }}
          onClick={handleEventDrawerToggle}
        >
          <ChevronLeftIcon />
        </Button>
      )}


      {/* Use the DetailsDrawer Component */}
      <DetailsDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        selectedItem={selectedItem}
      />
    </div>
  );
};

export default TrafficMap;
